import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import type { DynamicFiltersValue, FieldOption } from '../components/DynamicFiltersBar'
import { collectPairs, parseMulti, joinMulti } from '../lib/campos'
import {
  derivarJefesYMedicos,
  filtrarRepresentantesPorJefe,
  filtrarRepresentantesPorTipo,
  type TipoRepresentante,
} from '../lib/representantesMedifarmaPeru'

interface Params {
  configId: number
  /** Listado completo de participantes (para derivar pares ID/nombre de representante). */
  participantes: Array<Record<string, unknown>> | undefined
  /** Clave del campo representante (`campos_externos` filtrable). */
  repKey: string | undefined
  filtros: DynamicFiltersValue
  setFiltros: Dispatch<SetStateAction<DynamicFiltersValue>>
}

export interface CascadaSelect {
  key: string
  label: string
  options: FieldOption[]
  value?: string
  onChange: (v: string | undefined) => void
}

/**
 * Filtros cascada "Jefe de Ventas" / "Representante Médico" (Medifarma Perú, id 7).
 *
 * Al elegir un Jefe (Jefe de Zona de los RRVV) o un Supervisor (RRMM), la lista
 * del dropdown Representante se acota a sus RRVV/RRMM (`narrowRepOptions`) y el
 * filtro de datos usa el CONJUNTO de sus representantes. El upstream acepta varios
 * representantes (coma-separado), así que:
 *  - `inyectarReps(filtros)` inyecta el conjunto para las llamadas server-side
 *    (tablas, agregados de Ventas/Canjes, resumen).
 *  - `repSetCliente` expone el mismo conjunto para las superficies que se derivan
 *    del listado ya cargado (gráficas de Registros, vista "Por participante").
 * Si el usuario elige un representante puntual, ése filtra por sí solo y tanto
 * `inyectarReps` como `repSetCliente` quedan inactivos.
 */
export function useCascadaRepresentante({ configId, participantes, repKey, filtros, setFiltros }: Params) {
  // Selección múltiple de la fuerza de ventas: ramas (RRVV/RRMM), jefes de zona y
  // supervisores. Cada uno acumula varios valores (multi-selección).
  const [grupoTipos, setGrupoTipos] = useState<TipoRepresentante[]>([])
  const [jefesVentas, setJefesVentas] = useState<string[]>([])
  const [repsMedico, setRepsMedico] = useState<string[]>([])

  // Pares {value: id, label: nombre} de representante desde el listado.
  const repOptions = useMemo<FieldOption[]>(() => {
    if (configId !== 7 || !repKey || !participantes) return []
    return collectPairs(
      participantes,
      (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[repKey],
      (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${repKey}_nombre`],
    )
  }, [configId, repKey, participantes])

  const { opcionesJefe, opcionesMedico } = useMemo(
    () => derivarJefesYMedicos(configId, repOptions),
    [configId, repOptions],
  )

  const cascadaActiva =
    configId === 7 && (grupoTipos.length > 0 || jefesVentas.length > 0 || repsMedico.length > 0)

  // IDs de representante permitidos por la selección activa. Primero se acota a las
  // ramas (RRVV/RRMM) si hay "Tipo de fuerza"; luego, si además hay jefes o
  // supervisores elegidos, se acota a la UNIÓN de sus representantes.
  const repIdsPermitidos = useMemo(() => {
    if (!cascadaActiva) return []
    let permitidas = filtrarRepresentantesPorTipo(configId, repOptions, grupoTipos)
    if (jefesVentas.length > 0 || repsMedico.length > 0) {
      permitidas = filtrarRepresentantesPorJefe(configId, permitidas, jefesVentas, repsMedico)
    }
    return permitidas.map((o) => o.value)
  }, [cascadaActiva, configId, repOptions, grupoTipos, jefesVentas, repsMedico])

  // El conjunto se aplica sólo si el usuario NO eligió un representante puntual
  // (ése ya filtra server-side por sí solo).
  const usaConjunto = cascadaActiva && Boolean(repKey) && !filtros[repKey as string] && repIdsPermitidos.length > 0

  // Inyecta el conjunto de representantes del jefe/supervisor (coma-separado) en
  // los filtros que van al backend. El proxy expande el valor coma-separado a
  // parámetros repetidos (`?representante=1&representante=2`), formato que el
  // upstream requiere para filtrar por varios representantes (ver
  // `_split_multivalor` en backend/reportes_fidelizacion/routers/proxy.py). Así
  // las superficies server-side (tablas, agregados de Ventas/Canjes, resumen)
  // filtran por todo el conjunto del jefe.
  const inyectarReps = useCallback(
    (base: DynamicFiltersValue): DynamicFiltersValue => {
      if (!usaConjunto || !repKey) return base
      return { ...base, [repKey]: repIdsPermitidos.join(',') }
    },
    [usaConjunto, repKey, repIdsPermitidos],
  )

  // Mismo conjunto para las superficies inherentemente CLIENT-SIDE (gráficas de
  // Registros y vista "Por participante" de Participantes, que se derivan del
  // listado ya cargado, no de una llamada al upstream con este filtro).
  const repSetCliente = useMemo(
    () => (usaConjunto ? new Set(repIdsPermitidos) : null),
    [usaConjunto, repIdsPermitidos],
  )

  // Opciones de representante permitidas por (tipo, jefe, supervisor); base para
  // acotar el dropdown y para limpiar el representante puntual si deja de pertenecer.
  const permitidasPor = useCallback(
    (
      base: FieldOption[],
      tipos: TipoRepresentante[],
      jefes: string[],
      sups: string[],
    ): FieldOption[] => {
      let out = filtrarRepresentantesPorTipo(configId, base, tipos)
      if (jefes.length > 0 || sups.length > 0) {
        out = filtrarRepresentantesPorJefe(configId, out, jefes, sups)
      }
      return out
    },
    [configId],
  )

  // Limpia de los representantes puntuales (multi) los que ya no pertenecen al
  // conjunto permitido por la cascada.
  const limpiarRepPuntualSiSobra = useCallback(
    (permitido: FieldOption[]) => {
      if (!repKey) return
      const cur = parseMulti(filtros[repKey])
      if (cur.length === 0) return
      const permitidosSet = new Set(permitido.map((o) => o.value))
      const next = cur.filter((v) => permitidosSet.has(v))
      if (next.length === cur.length) return
      setFiltros((prev) => {
        const nx = { ...prev }
        const joined = joinMulti(next)
        if (joined) nx[repKey] = joined
        else delete nx[repKey]
        return nx
      })
    },
    [repKey, filtros, setFiltros],
  )

  // Al cambiar jefes/supervisores, limpiar los representantes puntuales que ya no pertenezcan.
  const aplicarCascada = useCallback(
    (jefes: string[], sups: string[]) => {
      setJefesVentas(jefes)
      setRepsMedico(sups)
      limpiarRepPuntualSiSobra(permitidasPor(repOptions, grupoTipos, jefes, sups))
    },
    [repOptions, grupoTipos, permitidasPor, limpiarRepPuntualSiSobra],
  )

  // Al cambiar "Tipo de fuerza", limpiar la rama que deje de estar seleccionada (los
  // jefes pertenecen a RRVV, los supervisores a RRMM) y los representantes puntuales
  // que dejen de pertenecer. Sin tipos elegidos, ambas ramas quedan permitidas.
  const aplicarGrupo = useCallback(
    (tipos: TipoRepresentante[]) => {
      setGrupoTipos(tipos)
      const permiteRRVV = tipos.length === 0 || tipos.includes('RRVV')
      const permiteRRMM = tipos.length === 0 || tipos.includes('RRMM')
      const jefes = permiteRRVV ? jefesVentas : []
      const sups = permiteRRMM ? repsMedico : []
      if (jefes !== jefesVentas) setJefesVentas(jefes)
      if (sups !== repsMedico) setRepsMedico(sups)
      limpiarRepPuntualSiSobra(permitidasPor(repOptions, tipos, jefes, sups))
    },
    [jefesVentas, repsMedico, repOptions, permitidasPor, limpiarRepPuntualSiSobra],
  )

  // Acota las opciones del dropdown Representante a la rama (tipo) y al jefe/supervisor elegidos.
  const narrowRepOptions = useCallback(
    (
      fieldOptions: Record<string, string[] | FieldOption[]> | undefined,
    ): Record<string, string[] | FieldOption[]> | undefined => {
      if (!repKey || configId !== 7 || !fieldOptions) return fieldOptions
      const base = (fieldOptions[repKey] as FieldOption[] | undefined) ?? repOptions
      return { ...fieldOptions, [repKey]: permitidasPor(base, grupoTipos, jefesVentas, repsMedico) }
    },
    [repKey, configId, repOptions, grupoTipos, jefesVentas, repsMedico, permitidasPor],
  )

  const cascadeSelects: CascadaSelect[] | undefined = useMemo(
    () =>
      configId === 7
        ? [
            {
              key: '__tipo_fuerza',
              label: 'Tipo de fuerza',
              options: [
                { value: 'RRVV', label: 'Jefes de Ventas (RRVV)' },
                { value: 'RRMM', label: 'Supervisores (RRMM)' },
              ],
              value: joinMulti(grupoTipos),
              onChange: (v: string | undefined) =>
                aplicarGrupo(parseMulti(v) as TipoRepresentante[]),
            },
            {
              key: '__jefe_ventas',
              label: 'Jefe de Ventas',
              // Oculto si se acotó a ramas que no incluyen RRVV.
              options: grupoTipos.length > 0 && !grupoTipos.includes('RRVV') ? [] : opcionesJefe,
              value: joinMulti(jefesVentas),
              onChange: (v: string | undefined) => aplicarCascada(parseMulti(v), repsMedico),
            },
            {
              key: '__rep_medico',
              label: 'Supervisor',
              // Oculto si se acotó a ramas que no incluyen RRMM.
              options: grupoTipos.length > 0 && !grupoTipos.includes('RRMM') ? [] : opcionesMedico,
              value: joinMulti(repsMedico),
              onChange: (v: string | undefined) => aplicarCascada(jefesVentas, parseMulti(v)),
            },
          ]
        : undefined,
    [configId, grupoTipos, opcionesJefe, opcionesMedico, jefesVentas, repsMedico, aplicarGrupo, aplicarCascada],
  )

  return { cascadeSelects, inyectarReps, repSetCliente, narrowRepOptions, cascadaActiva }
}
